import { motion } from "framer-motion";
import type { SolutionData } from "@/data/solutionsData";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";

interface Props {
  solution: SolutionData;
  onCtaClick?: () => void;
}

const anim = (i: number) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4, delay: i * 0.1 },
});

const SolutionClientCases = ({ solution, onCtaClick }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);
  if (!solution.clientCases?.length) return null;

  return (
    <section className="py-16 sm:py-20 scroll-mt-28">
      <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-8 h-0.5 rounded-full bg-primary" />
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("clientCases")}</p>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
          {solution.clientCasesTitle || t("recentClientCases")}
        </h2>
        <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-3xl">
          {solution.clientCasesSubtitle || t("defaultClientCasesSubtitle")}
        </p>
        {solution.clientCases.length === 1 ? (
          solution.clientCases.map((c, i) => (
            <motion.div
              key={c.title}
              className="group rounded-xl overflow-hidden border border-border/40 bg-muted/30 hover:shadow-md transition-all duration-200"
              {...anim(i)}
            >
              <div className="grid md:grid-cols-[2fr_3fr]">
                <div className="relative">
                  <img src={c.image} alt={c.title} className="w-full h-full min-h-[220px] object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" />
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide bg-primary text-primary-foreground">
                    {c.tag}
                  </span>
                </div>
                <div className="p-6 sm:p-8 flex flex-col justify-center">
                  <h3 className="text-lg font-semibold text-foreground mb-1.5">{c.title}</h3>
                  <p className="text-sm italic text-muted-foreground mb-4">{c.subtitle}</p>
                  <ul className="text-sm text-muted-foreground space-y-3">
                    {c.bullets.map((b, j) => {
                      const asciiIdx = b.indexOf(":"); const cjkIdx = b.indexOf("："); const colonIdx = asciiIdx === -1 ? cjkIdx : (cjkIdx === -1 ? asciiIdx : Math.min(asciiIdx, cjkIdx));
                      const label = colonIdx > -1 ? b.slice(0, colonIdx) : null;
                      const rest = colonIdx > -1 ? b.slice(colonIdx + 1) : b;
                      return (
                        <li key={j} className="flex gap-2.5">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0 bg-primary" />
                          <span>
                            {label && <span className="font-semibold text-foreground">{label}{b.charAt(colonIdx)}</span>}
                            {rest}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {onCtaClick && (
                    <button onClick={onCtaClick} className="mt-5 text-sm font-medium text-primary hover:underline self-start">
                      {t("getInTouchArrow")}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="grid sm:grid-cols-2 gap-6">
            {solution.clientCases.map((c, i) => (
              <motion.div
                key={c.title}
                className="group rounded-xl overflow-hidden border border-border/40 bg-muted/30 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                {...anim(i)}
              >
                <div className="relative">
                  <img src={c.image} alt={c.title} className="w-full h-52 object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" />
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide bg-primary text-primary-foreground">
                    {c.tag}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-1.5">{c.title}</h3>
                  <p className="text-sm italic text-muted-foreground mb-3">{c.subtitle}</p>
                  <ul className="text-sm text-muted-foreground space-y-3">
                    {c.bullets.map((b, j) => {
                      const asciiIdx = b.indexOf(":"); const cjkIdx = b.indexOf("："); const colonIdx = asciiIdx === -1 ? cjkIdx : (cjkIdx === -1 ? asciiIdx : Math.min(asciiIdx, cjkIdx));
                      const label = colonIdx > -1 ? b.slice(0, colonIdx) : null;
                      const rest = colonIdx > -1 ? b.slice(colonIdx + 1) : b;
                      return (
                        <li key={j} className="flex gap-2.5">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0 bg-primary" />
                          <span>
                            {label && <span className="font-semibold text-foreground">{label}{b.charAt(colonIdx)}</span>}
                            {rest}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {onCtaClick && (
                    <button onClick={onCtaClick} className="mt-4 text-sm font-medium text-primary hover:underline">
                      {t("getInTouchArrow")}
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default SolutionClientCases;
